import React, { useState, useEffect } from 'react';
import { Responsive, WidthProvider, Layout } from 'react-grid-layout';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Plus, Edit3, Save, RotateCcw } from 'lucide-react';
import { WidgetContainer } from './WidgetContainer';
import { WidgetSidebar } from './WidgetSidebar';
import { WidgetConfig, DashboardLayout } from '@/lib/widgets/types';
import { useToast } from '@/hooks/use-toast';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';

const ResponsiveGridLayout = WidthProvider(Responsive);

interface DashboardEditorProps {
  enabledSystems: string[];
  initialLayout?: DashboardLayout;
  onSaveLayout?: (layout: DashboardLayout) => void;
  className?: string;
}

export function DashboardEditor({
  enabledSystems,
  initialLayout,
  onSaveLayout,
  className = ''
}: DashboardEditorProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [widgets, setWidgets] = useState<WidgetConfig[]>(initialLayout?.widgets || []);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { toast } = useToast();

  // Synchronize widgets state when initialLayout changes (but not during editing)
  useEffect(() => {
    if (initialLayout?.widgets && !isEditing) {
      console.log('🔄 Syncing widgets with initialLayout:', initialLayout.widgets);
      setWidgets(initialLayout.widgets);
      setHasUnsavedChanges(false);
    }
  }, [initialLayout, isEditing]);

  const handleLayoutChange = (currentLayout: Layout[], allLayouts: { [key: string]: Layout[] }) => {
    if (!isEditing) return;
    
    console.log('Layout changed:', currentLayout, allLayouts);
    
    // Apply layout changes to widgets using currentLayout
    const updatedWidgets = widgets.map(widget => {
      const layoutItem = currentLayout.find(item => item.i === widget.id);
      if (layoutItem) {
        return {
          ...widget,
          position: { x: layoutItem.x, y: layoutItem.y },
          size: { 
            ...widget.size, 
            w: layoutItem.w, 
            h: layoutItem.h 
          }
        };
      }
      return widget;
    });
    
    setWidgets(updatedWidgets);
    setHasUnsavedChanges(true);
  };

  const handleAddWidget = (widgetType: string) => {
    // Calculate the next Y position to place widget at bottom of grid
    const nextY = widgets.length > 0 
      ? Math.max(...widgets.map(w => w.position.y + w.size.h)) 
      : 0;

    const newWidget: WidgetConfig = {
      id: `widget-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: widgetType,
      title: `Widget ${widgets.length + 1}`,
      size: { w: 4, h: 3 },
      position: { x: 0, y: nextY },
      props: {}
    };

    setWidgets(prev => [...prev, newWidget]);
    setHasUnsavedChanges(true);
    setIsSidebarOpen(false);
    
    toast({
      title: "Widget adicionado",
      description: "Widget foi adicionado ao dashboard"
    });
  };

  const handleRemoveWidget = (widgetId: string) => {
    setWidgets(prev => prev.filter(widget => widget.id !== widgetId));
    setHasUnsavedChanges(true);
    
    toast({
      title: "Widget removido",
      description: "Widget foi removido do dashboard"
    });
  };

  const handleSave = () => {
    try {
      console.log('Saving layout with widgets:', widgets);
      
      const layout: DashboardLayout = {
        id: initialLayout?.id || `layout-${Date.now()}`,
        widgets,
        lastModified: new Date().toISOString()
      };

      console.log('Layout to save:', layout);
      
      if (onSaveLayout) {
        onSaveLayout(layout);
        console.log('Layout saved successfully');
      } else {
        console.warn('No onSaveLayout function provided');
      }
      
      setHasUnsavedChanges(false);
      setIsEditing(false);
      
      toast({
        title: "Layout salvo",
        description: "Suas alterações foram salvas com sucesso"
      });
    } catch (error) {
      console.error('Error saving layout:', error);
      toast({
        title: "Erro ao salvar",
        description: "Não foi possível salvar o layout",
        variant: "destructive"
      });
    }
  };

  const handleReset = () => {
    setWidgets(initialLayout?.widgets || []);
    setHasUnsavedChanges(false);
    
    toast({
      title: "Layout resetado",
      description: "Dashboard foi restaurado para o estado salvo"
    });
  };

  const generateLayout = (): Layout[] => {
    return widgets.map(widget => ({
      i: widget.id,
      x: widget.position.x,
      y: widget.position.y,
      w: widget.size.w,
      h: widget.size.h,
      minW: widget.size.minW,
      minH: widget.size.minH,
      maxW: widget.size.maxW,
      maxH: widget.size.maxH
    }));
  };

  if (widgets.length === 0 && !isEditing) {
    return (
      <div className={`space-y-6 ${className}`}>
        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-bold">Dashboard</h2>
          <Button onClick={() => setIsEditing(true)} className="gap-2">
            <Edit3 className="h-4 w-4" />
            Personalizar Dashboard
          </Button>
        </div>
        
        <Card className="p-12 text-center border-dashed">
          <div className="space-y-4">
            <div className="mx-auto w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
              <Plus className="h-6 w-6 text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-semibold">Dashboard Vazio</h3>
              <p className="text-sm text-muted-foreground mt-1">
                Comece adicionando widgets para personalizar seu dashboard
              </p>
            </div>
            <Button onClick={() => setIsEditing(true)} className="gap-2">
              <Plus className="h-4 w-4" />
              Adicionar Primeiro Widget
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className={`space-y-6 ${className}`}>
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Dashboard</h2>
        
        <div className="flex gap-2">
          {isEditing ? (
            <>
              <Button
                variant="outline"
                onClick={() => setIsSidebarOpen(true)}
                className="gap-2"
              >
                <Plus className="h-4 w-4" />
                Adicionar Widget
              </Button>
              
              {hasUnsavedChanges && (
                <Button variant="outline" onClick={handleReset} className="gap-2">
                  <RotateCcw className="h-4 w-4" />
                  Resetar
                </Button>
              )}
              
              <Button onClick={handleSave} className="gap-2">
                <Save className="h-4 w-4" />
                Salvar Layout
              </Button>
            </>
          ) : (
            <Button onClick={() => setIsEditing(true)} variant="outline" className="gap-2">
              <Edit3 className="h-4 w-4" />
              Editar Dashboard
            </Button>
          )}
        </div>
      </div>

      <div className="min-h-[400px]">
        <ResponsiveGridLayout
          className="layout"
          layouts={{ lg: generateLayout() }}
          onLayoutChange={handleLayoutChange}
          cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
          rowHeight={80}
          isDraggable={isEditing}
          isResizable={isEditing}
          compactType={null}
          preventCollision={true}
          margin={[16, 16]}
          draggableCancel=".widget-remove-button, .widget-content"
        >
          {widgets.map(widget => (
            <div key={widget.id}>
              <WidgetContainer
                id={widget.id}
                config={widget}
                isEditing={isEditing}
                onRemove={() => handleRemoveWidget(widget.id)}
              />
            </div>
          ))}
        </ResponsiveGridLayout>
      </div>

      <WidgetSidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        enabledSystems={enabledSystems}
        existingWidgets={widgets.map(w => w.type)}
        onAddWidget={handleAddWidget}
      />
    </div>
  );
}